"use client";

import { getCountryNameFromCountryCode } from "@utils/data/prefixes";
import { getCookie } from "cookies-next";
import { useSearchParams } from "next/navigation";
import { FC, ReactNode, createContext, useEffect, useState } from "react";

import { getDeviceType } from "@/hooks/getDeviceType";
import useClientInfo from "@/hooks/useClientInfo";

interface Props {
  children: ReactNode;
}
interface UTMParametersContextProps {
  utmParameters: { [key: string]: string };
}
export const UTMParametersContext = createContext<UTMParametersContextProps>({
  utmParameters: {},
});

export const utmParametersMapping = {
  campaign_id: "campaignId",
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  websource: "webSource",
  adgroup: "adGroup",
  campaign_name: "campaignNameHOD",
  goal: "goal",
  digital_source: "digitalSource",
  channelcluster: "channelCluster",
  bannersize: "bannerSize",
  keyword: "keyword",
  placement: "placement",
  adposition: "adPosition",
  matchtype: "matchType",
  network: "network",
  bidtype: "bidType",
  gclid: "GCLID",
  fbclid: "fbclid",
  leadSource: "leadSource",
  ga_client_id: "ga_client_id",
  fbid: "fbid",
  refid: "refid",
  ad_id: "adid",
  browser: "browser",
  device: "device",
  lastMileConversion: "lastMileConversion",
  os: "os",
  projectStatus: "projectStatus",
  resolution: "resolution",
  websiteLanguage: "websiteLanguage",
  leadGenIdentifier: "leadGenIdentifier",
  landingPageURL: "landingPageURL",
  fullLandingPageUrl: "fullLandingPageUrl",
  countryNameSync: "countryNameSync",
  citySync: "citySync",
  ipAddress: "ipAddress",
};

// default campaign_id as per https://dev.azure.com/damacgroupdevops/DPWeb_TO_Ecom_Dev/_workitems/edit/75175
// TASK 92401
// const defaultUTMParameters = { campaign_id: "a121n0000038zCp" };

// TASK 103219
// Dynamic phone number for Call CTA based on UTM Medium - Only Paid Search and Paid Social
// To be dynamic with the Contentfull Values
const fallbackCampaignParametersTable = [
  [undefined, undefined, "a120Y000000uLMj", "DAMACProperties.Dig-Dir/Org-Dir-Jan22", "+97145205400"],
  ["paid-search", "Paid Search", "a121n00000Dwy45", "DefaultDigitalLeads.Dig-Search-Goog-ENG", "+97144016204"],
  ["paid-social", "Social", "a121n00000D9pUv", "Default_Social.Dig-Socl-Aug18", "+97144016205"],
  ["email", "E-mail", "a1207000000cX0R", "DefaultDigitalLeads.Dig-Eml-UAE-ENG-Jun8", "+97144016207"],
  ["paid-display", "Display", "a1207000000bnOn", "DefaultDigitalLeads.Dig-Dspl-Goog-UAE-ENG-May19", "+97144016208"],
  ["online-listings", "Aggregators/Affiliates", "a1207000000d8a4", "DefaultDigitalLeads.Dig-OnlList-ENG-May19", "+97144016209"],
  ["pmax", "PMAX", "a12TY000003dw41", "DefaultDigitalLeads.Dig-ENG-Nov11", "+97144016208"],
  ["banners", "Aggregators", "a1207000000d8a4", "DefaultDigitalLeads.Dig-OnlList-ENG-May19", "+97144016209"],
  ["d360", "D360", "a120Y000000uLMj", "DAMACProperties.Dig-Dir/Org-Dir-Jan22", "+97180032622"],
  ["sf_payment", "SF Payment", "a120Y000000uLMj", "DAMACProperties.Dig-Dir/Org-Dir-Jan22", "+97180032622"],
];

export const getFallbackCampaignParameters = (utmMedium: string | undefined) => {
  const row = fallbackCampaignParametersTable.find((x) => x[0]?.toLowerCase() == utmMedium?.toLowerCase());
  return (
    row
      ? {
          campaign_type: row[1],
          campaign_id: row[2],
          campaign_name: row[3],
          virtual_number: row[4],
        }
      : {
          campaign_id: fallbackCampaignParametersTable[0][2],
          campaign_name: fallbackCampaignParametersTable[0][3],
          virtual_number: fallbackCampaignParametersTable[0][4],
        }
  ) as { [key: string]: string };
};

const UTMParametersContextProvider: FC<Props> = ({ children }) => {
  // BUG 75175 89195
  // const [utmParameters, setUTMParameters] = useState(defaultUTMParameters);
  // useEffect(() => {
  //   const parameters = JSON.parse(localStorage.getItem("utmParameters") || "{}");
  //   if (Object.keys(parameters).length > 0) {
  //     setUTMParameters({ ...defaultUTMParameters, ...parameters });
  //   }
  // }, []);
  const [utmParameters, setUTMParameters] = useState(() => {
    const parameters = JSON.parse(typeof localStorage !== "undefined" ? localStorage.getItem("utmParameters") ?? "{}" : "{}");
    // console.log("UTMParametersContextProvider parameters", parameters);
    return parameters;
  });

  if (!utmParameters?.landingPageURL) {
    if (!!typeof window && typeof window !== "undefined") {
      const existingLandingPageUrl = window?.sessionStorage?.getItem("landingPageUrl");
      if (!existingLandingPageUrl) {
        window?.sessionStorage?.setItem("landingPageUrl", window?.location?.href?.split("?")[0]);
      }
      utmParameters.landingPageURL = existingLandingPageUrl;

      utmParameters.fullLandingPageUrl = window.location.href.split("?")[0];
    }
  }
  useEffect(() => {
    if (!!typeof window) {
      const existingLandingPageUrl = window?.sessionStorage?.getItem("landingPageUrl");
      if (!existingLandingPageUrl) {
        window?.sessionStorage?.setItem("landingPageUrl", window?.location?.href?.split("?")[0]);
      }

      // TASK 92353
      // do not send full landing page url in both lead and checkout journeys
      // as it is causing an error due to insufficient length in SF

      setUTMParameters((prev: any) => ({
        ...prev,
        landingPageURL: existingLandingPageUrl,
        fullLandingPageUrl: window?.location?.href?.split("?")[0],
      }));
    }
  }, []);

  const client = useClientInfo({});
  useEffect(() => {
    if (!client || typeof window === "undefined") return;

    const info = client.getResult();

    setUTMParameters((prev: any) => ({
      ...prev,
      browser: info?.browser ? `${info?.browser?.name} ${info?.browser?.version}` : "",
      device: getDeviceType(info?.device) ?? "",
      fbclid: getCookie("_fbc") ?? "",
      fbid: getCookie("_fbp") ?? "",
      ga_client_id: getCookie("_ga") ?? "",
      os: info?.os?.name ?? "",
      resolution: window?.screen ? `${window?.screen?.width * window?.devicePixelRatio} x ${window?.screen?.height * window?.devicePixelRatio}` : "",
      uniq_vis_id: getCookie("uniq_vis_id") ?? "",
      websiteLanguage: getCookie("language") ?? "",
      // BUG 93350
      leadGenIdentifier: window?.location?.hostname,
      citySync: getCookie("city") ?? "",
      // BUG 109562
      ipAddress: getCookie("ip") ?? "",
      countryNameSync: getCountryNameFromCountryCode((getCookie("countryJeo") ?? "").toUpperCase()) ?? "",
    }));
  }, [client]);

  // START: Wait for GA cookie to appear
  useEffect(() => {
    if (typeof window === "undefined") return;

    let attempts = 0;
    const maxAttempts = 20; // 20 * 250ms = 5 seconds max
    const interval = setInterval(() => {
      const ga = getCookie("_ga");

      if (ga || attempts >= maxAttempts) {
        clearInterval(interval);

        if (ga) {
          setUTMParameters((prev: any) => ({
            ...prev,
            ga_client_id: ga,
          }));
        }
      }

      attempts++;
    }, 250);

    return () => clearInterval(interval);
  }, []);
  // END

  const searchParams = useSearchParams();
  useEffect(() => {
    const parameters = Object.fromEntries(
      [
        "campaign_id",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "websource",
        "adgroup",
        "campaign_name",
        "goal",
        "digital_source",
        "channelcluster",
        "bannersize",
        "keyword",
        "placement",
        "adposition",
        "matchtype",
        "network",
        "bidtype",
        "gclid",
        "fbclid",
        "leadSource",
        "ga_client_id",
        "fbid",
        "refid",
        "ad_id",
        "countryNameSync",
        "citySync",
        "ipAddress",
      ]
        .map((x) => [x, searchParams.get(x)])
        .filter(([k, v]) => v !== null),
    );
    if (Object.keys(parameters).length) {
      localStorage.setItem("utmParameters", JSON.stringify(parameters));
      setUTMParameters(parameters);
    }
  }, [searchParams]);

  // console.log("utmParameters", utmParameters);
  return <UTMParametersContext.Provider value={{ utmParameters }}>{children}</UTMParametersContext.Provider>;
};
export default UTMParametersContextProvider;